import type { IMesh } from '@seddi/mesh';
import type { ButtonWithSurfaceMaterial, Environment, Model } from '@seddi/scene';
import type { TSurfaceMaterial, TTexture } from '@seddi/surface-material';
import { LoadingManager, Material, MeshBasicMaterial } from '@seddi/three';
import { RecolorModule } from '@seddi/ui-recolor';
import log from 'loglevel';

import { ThreeRenderer } from '../renderer3/threeRenderer';
import { ButtonsContext } from './buttonsContext';
import type { ButtonGfx } from './buttonTypes';
import { EnvironmentsContext } from './environmentsContext';
import type { EnvironmentGfx } from './environmentTypes';
import { FabricsContext } from './fabricsContext';
import type { FabricWithColor, FabricGfx } from './fabricTypes';
import { GarmentGeometriesContext, GarmentGeometryDesc } from './garmentGeometriesContext';
import type { GarmentGeometryGfx } from './garmentGeometryTypes';
import { LogosContext } from './logosContext';
import type { LogoDesc } from './logoTypes';
import { MaterialsContext } from './materialsContext';
import { MeshesContext } from './meshesContext';
import { ModelsContext } from './modelsContext';
import type { ResourceKind } from './resourceEnum';
import { ResourceEvent } from './resourceEvent';
import { type ResourceContextConfig, create as createConfig } from './resourcesContextConfig';
import { type StitchesTexturesDesc, StitchesTexturesContext } from './stitchesTexturesContext';
import type { StitchesTextures } from './stitchesTexturesTypes';
import { TexturesContext } from './texturesContext';

export class ResourcesContext extends EventTarget {
  readonly config: ResourceContextConfig;
  readonly renderer: ThreeRenderer;

  /**
   * Buttons.
   */
  readonly buttons: ButtonsContext;

  /**
   * Environmnents.
   */
  readonly environments: EnvironmentsContext;

  /**
   * Fabrics.
   */
  readonly fabrics: FabricsContext;

  /**
   * Garment geometries.
   */
  readonly garmentGeometries: GarmentGeometriesContext;

  /**
   * Logos.
   */
  readonly logos: LogosContext;

  /**
   * Materials.
   */
  readonly materials: MaterialsContext;

  /**
   * Self-managed materials registry.
   */
  readonly materialsRegistry: Map<string, Material> = new Map<string, Material>();

  /**
   * Meshes.
   */
  readonly meshes: MeshesContext;

  /**
   * Meshes.
   */
  readonly models: ModelsContext;

  /**
   * Textures.
   */
  readonly textures: TexturesContext;

  /**
   * Stitches textures.
   */
  readonly stitchesTextures: StitchesTexturesContext;

  /**
   * Specifies viewport needs to be drawed.
   */
  needsUpdate: boolean = false;

  constructor(renderer: ThreeRenderer, config?: Partial<ResourceContextConfig>) {
    super();

    this.config = createConfig(config);
    this.renderer = renderer;

    this.manager = new LoadingManager();
    this.manager.onError = (url) => {
      log.error(`LoadingManager ${url} error`);
    };
    this.manager.onProgress = (url, loaded, total) => {
      log.debug(`LoadingManager ${url} loaded [${loaded}/${total}].`);
    };

    this.onLoad = this.onLoad.bind(this);
    this.buttons = new ButtonsContext(this.manager, this.renderer.renderer);
    this.buttons.onLoad = this.onLoad;
    this.environments = new EnvironmentsContext(this.manager, this.renderer.renderer);
    this.environments.onLoad = this.onLoad;
    const fabricsConfig = {
      mipmapsHighQuality: this.config.fabricMipmapsHighQuality,
      mipmapsUnsharp: this.config.fabricMipmapsUnsharp,
    };
    this.fabrics = new FabricsContext(
      fabricsConfig,
      this.manager,
      RecolorModule.context,
      this.renderer.renderer,
    );
    this.fabrics.onLoad = this.onLoad;
    this.garmentGeometries = new GarmentGeometriesContext(this.manager, this.config);
    this.garmentGeometries.onLoad = this.onLoad;
    this.logos = new LogosContext(this.manager, this.renderer.renderer);
    this.logos.onLoad = this.onLoad;
    this.materials = new MaterialsContext(this.manager, this.renderer.renderer);
    this.materials.onLoad = this.onLoad;
    this.meshes = new MeshesContext(this.manager);
    this.meshes.onLoad = this.onLoad;
    this.models = new ModelsContext(this.manager);
    this.models.onLoad = this.onLoad;
    this.stitchesTextures = new StitchesTexturesContext(this.manager);
    this.stitchesTextures.onLoad = this.onLoad;
    this.textures = new TexturesContext(this.manager, this.renderer.renderer);
    this.textures.onLoad = this.onLoad;

    this.isDeferredDirty = false;
    this._materialDef = new MeshBasicMaterial({ color: 0xffff00 });
  }

  /**
   * Number of loaded items.
   */
  get itemsLoaded(): number {
    return this._itemsLoaded;
  }

  /**
   * Number of total items.
   */
  get itemsTotal(): number {
    return this._itemsTotal;
  }

  get materialDef(): Material {
    return this._materialDef;
  }

  dispose() {
    this.buttons.dispose();
    this.environments.dispose();
    this.fabrics.dispose();
    this.garmentGeometries.dispose();
    this.logos.dispose();
    this.materials.dispose();
    this.meshes.dispose();
    this.models.dispose();
    this.stitchesTextures.dispose();
    this.textures.dispose();
  }

  getRefButton(id: string): ButtonGfx {
    return this.buttons.getRef(id);
  }

  retRefButton(id: string) {
    this.buttons.retRef(id);
  }

  findButton(id: string): ButtonGfx {
    return this.buttons.find(id);
  }

  loadButton(desc: ButtonWithSurfaceMaterial): Promise<ButtonGfx> {
    this._itemsTotal++;
    return this.buttons.loadAsync(desc);
  }

  hasButtonRequest(id: string): boolean {
    return this.buttons.hasRequest(id);
  }

  loadButtonDeferred(desc: ButtonWithSurfaceMaterial) {
    this.buttons.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefEnvironment(id: string): EnvironmentGfx {
    return this.environments.getRef(id);
  }

  retRefEnvironment(id: string) {
    this.environments.retRef(id);
  }

  findEnvironment(id: string): EnvironmentGfx {
    return this.environments.find(id);
  }

  loadEnvironment(desc: Environment): Promise<EnvironmentGfx> {
    this._itemsTotal++;
    return this.environments.loadAsync(desc);
  }

  hasEnvironmentRequest(id: string): boolean {
    return this.environments.hasRequest(id);
  }

  loadEnvironmentDeferred(desc: Environment) {
    this.environments.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefFabric(id: string): FabricGfx {
    return this.fabrics.getRef(id);
  }

  retRefFabric(id: string) {
    this.fabrics.retRef(id);
  }

  findFabric(id: string) {
    return this.fabrics.find(id);
  }

  loadFabric(desc: FabricWithColor): Promise<FabricGfx> {
    this._itemsTotal++;
    return this.fabrics.loadAsync(desc);
  }

  hasFabricRequest(id: string) {
    return this.fabrics.hasRequest(id);
  }

  loadFabricDeferred(desc: FabricWithColor) {
    this.fabrics.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefGarmentGeometry(id: string): GarmentGeometryGfx {
    return this.garmentGeometries.getRef(id);
  }

  retRefGarmentGeometry(id: string) {
    this.garmentGeometries.retRef(id);
  }

  findGarmentGeometry(id: string): GarmentGeometryGfx {
    return this.garmentGeometries.find(id);
  }

  loadGarmentGeometry(desc: GarmentGeometryDesc): Promise<GarmentGeometryGfx> {
    this._itemsTotal++;
    return this.garmentGeometries.loadAsync(desc);
  }

  hasGarmentGeometryRequest(id: string): boolean {
    return this.garmentGeometries.hasRequest(id);
  }

  loadGarmentGeometryDeferred(desc: GarmentGeometryDesc) {
    this.garmentGeometries.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefLogo(id: string) {
    return this.logos.getRef(id);
  }

  retRefLogo(id: string) {
    this.logos.retRef(id);
  }

  findLogo(id: string) {
    return this.logos.find(id);
  }

  loadLogo(desc: LogoDesc) {
    this._itemsTotal++;
    this.logos.loadAsync(desc);
  }

  hasLogoRequest(id: string) {
    return this.logos.hasRequest(id);
  }

  loadLogoDeferred(desc: LogoDesc) {
    this.logos.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefMaterial(id: string) {
    return this.materials.getRef(id);
  }

  retRefMaterial(id: string) {
    this.materials.retRef(id);
  }

  findMaterial(id: string) {
    return this.materials.find(id);
  }

  loadMaterial(desc: TSurfaceMaterial) {
    this._itemsTotal++;
    this.materials.loadAsync(desc);
  }

  hasMaterialRequest(id: string) {
    return this.materials.hasRequest(id);
  }

  loadMaterialDeferred(desc: TSurfaceMaterial) {
    this.materials.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefMesh(id: string) {
    return this.meshes.getRef(id);
  }

  retRefMesh(id: string) {
    this.meshes.retRef(id);
  }

  findMesh(id: string) {
    return this.meshes.find(id);
  }

  loadMesh(desc: IMesh) {
    this._itemsTotal++;
    return this.meshes.loadAsync(desc);
  }

  hasMeshRequest(id: string) {
    return this.meshes.hasRequest(id);
  }

  loadMeshDeferred(desc: IMesh) {
    this.meshes.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefModel(id: string) {
    return this.models.getRef(id);
  }

  retRefModel(id: string) {
    this.models.retRef(id);
  }

  findModel(id: string) {
    return this.models.find(id);
  }

  loadModel(desc: Model) {
    this._itemsTotal++;
    this.models.loadAsync(desc);
  }

  hasModelRequest(id: string) {
    return this.models.hasRequest(id);
  }

  loadModelDeferred(desc: Model) {
    this.models.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  findTexture(id: string) {
    return this.textures.find(id);
  }

  loadTexture(desc: TTexture) {
    this._itemsTotal++;
    this.textures.loadAsync(desc);
  }

  loadTextureDeferred(desc: TTexture) {
    this.textures.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  getRefStitchesTextures(id: string): StitchesTextures {
    return this.stitchesTextures.getRef(id);
  }

  retRefStitchesTextures(id: string) {
    this.stitchesTextures.retRef(id);
  }

  findStitchesTextures(id: string) {
    return this.stitchesTextures.find(id);
  }

  loadStitchesTextures(desc: StitchesTexturesDesc) {
    this._itemsTotal++;
    this.stitchesTextures.loadAsync(desc);
  }

  hasStitchesTexturesRequest(id: string) {
    return this.stitchesTextures.hasRequest(id);
  }

  loadStitchesTexturesDeferred(desc: StitchesTexturesDesc) {
    this.stitchesTextures.loadDeferred(desc);
    this.isDeferredDirty = true;
    this._itemsTotal++;
  }

  hasLoadPending() {
    return this.isDeferredDirty;
  }

  /*
   * Commit pending resource requests.
   */
  commit() {
    if (!this.isDeferredDirty) {
      return;
    }

    this.garmentGeometries.commit();
    this.buttons.commit();
    this.environments.commit();
    this.fabrics.commit();
    this.logos.commit();
    this.materials.commit();
    this.meshes.commit();
    this.models.commit();
    this.stitchesTextures.commit();
    this.textures.commit();
    this.isDeferredDirty = false;
  }

  private onLoad(id: string, kind: ResourceKind) {
    // resource change events
    {
      const event = new ResourceEvent('resourcechange', id, kind, 'ready');
      this.dispatchEvent(event);
    }
    // load progress events
    {
      this._itemsLoaded++;
      const event = new ProgressEvent('resourcesprogress', {
        lengthComputable: true,
        loaded: this._itemsLoaded,
        total: this._itemsTotal,
      });
      this.dispatchEvent(event);
    }
  }

  //
  // Internal
  //

  private _materialDef: Material;

  private isDeferredDirty: boolean;
  private manager: LoadingManager;

  private _itemsLoaded = 0;
  private _itemsTotal = 0;
}
